import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { BankCardIcon, Exit2Icon, Star2Icon } from "@/shared/icons";
import { Button, Modal } from "@/shared/ui";
import KeyActionPanel, { KeyAction } from "@/shared/ui/KeyActionPanel";
import { estateEnterInfoSelector, setEstateInfo, setShowSaleConfirm } from "@/domains/estate/slice";
import useEstateEnter from "@/domains/estate/hooks/useEstateEnter";
import EstateMainInfo from "@/domains/estate/components/EstateMainInfo";
import EstateView from "@/domains/estate/components/EstateView";
import KeyCode from "@/assets/constants/key-code";

export default function EstateSaleModalContainer({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation("estate");
  const estate = useAppSelector(estateEnterInfoSelector);
  const [enterEstate] = useEstateEnter();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (estate) {
      dispatch(setEstateInfo(estate));
    }
  }, [dispatch, estate]);

  const actions = new Map<KeyCode, KeyAction>([
    [
      KeyCode.E,
      {
        label: t("estate.enter"),
        keyName: "E",
        action: enterEstate,
        color: "green",
      },
    ],
    [
      KeyCode.G,
      {
        label: t(`estate.type.${estate.type}.buy`),
        keyName: "G",
        action: () => {
          dispatch(setShowSaleConfirm(true));
        },
        color: "green",
      },
    ],
    [
      KeyCode.ESCAPE,
      {
        label: t("estate.close"),
        keyName: "ESC",
        action: () => {
          dispatch(setEstateInfo(null));
        },
        color: "green",
      },
    ],
  ]);

  const title = (
    <div className="flex items-center gap-2.5 w-full">
      <div className="text-18 text-white font-bold">
        {t(`estate.type.${estate.type}.name`)} #{estate.id}
      </div>
      <div className=" bg-white flex items-center gap-1 py-1 pl-1 pr-1.5 rounded-[0.25rem] uppercase">
        <Star2Icon size={12} className="text-dark-800" />
        <div className="text-12 text-[#0F0F0F] font-bold leading-0">
          {t(`estate.level.${estate.level}`)}
        </div>
      </div>
    </div>
  );

  const showFooter = estate.isResident || estate.isSale || !estate.isLocked;

  const footer = showFooter ? (
    <div className="grid grid-cols-2 gap-2.5">
      <Button
        data-mz-accent="success"
        className="h-[2.625rem] flex items-center gap-2.5"
        onClick={() => dispatch(setShowSaleConfirm(true))}
      >
        <BankCardIcon size={16} />
        {t(`estate.type.${estate.type}.buy`)}
      </Button>
      <Button
        variant="soft"
        color="gray"
        className="h-[2.625rem] flex items-center gap-2.5"
        onClick={enterEstate}
      >
        <Exit2Icon size={16} />
        {t("estate.enterEstate")}
      </Button>
    </div>
  ) : null;

  return (
    <>
      <Modal
        open
        onClose={onClose}
        style={{
          maxHeight: "calc(100vh - 2 * var(--boundary-offset-y) - 6rem)",
        }}
        overlay={false}
        header={<EstateView estate={estate} isResidence={false} />}
        footer={footer}
        title={title}
      >
        <EstateMainInfo info={estate} hideDetails />
      </Modal>
      <KeyActionPanel
        name="estate-sale-modal"
        actions={actions}
        className="fixed bottom-[--boundary-offset-y] left-[50%] -translate-x-[50%] z-[51]"
      />
    </>
  );
}
